import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Pagination,
  Box,
  Paper,
  Skeleton,
  useTheme,
  TextField,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import { FilterList, Clear } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { followService, UserSuggestion, UserSearchResult } from '../../services';
import { ProfileCard } from '../../components/common/ProfileCard';

export const ProfileSuggestions: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');

  const [users, setUsers] = useState<(UserSuggestion | UserSearchResult)[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filter states
  const [workMode, setWorkMode] = useState<string>('');
  const [noticeType, setNoticeType] = useState<string>('');
  const [jobType, setJobType] = useState<string>('');
  const [experienceLevel, setExperienceLevel] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [page, query, workMode, noticeType, jobType, experienceLevel, location]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Build filter parameters
      const filters: any = { page, limit: 8 };
      if (workMode) filters.workMode = workMode;
      if (noticeType) filters.noticeType = noticeType;
      if (jobType) filters.jobType = jobType;
      if (experienceLevel) filters.experienceLevel = experienceLevel;
      if (location) filters.location = location;

      if (query) {
        // Search mode
        const response = await followService.searchUsers({ query, ...filters });
        if (response.success) {
          setUsers(response.data.users);
          setTotalPages(response.data.pagination.pages);
          setTotal(response.data.pagination.total);
        }
      } else {
        // Suggestions mode
        const response = await followService.getSuggestions(filters);
        if (response.success) {
          setUsers(response.data.suggestions);
          setTotalPages(response.data.pagination.pages);
          setTotal(response.data.pagination.total);
        }
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const clearAllFilters = () => {
    setWorkMode('');
    setNoticeType('');
    setJobType('');
    setExperienceLevel('');
    setLocation('');
    setPage(1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (workMode) count++;
    if (noticeType) count++;
    if (jobType) count++;
    if (experienceLevel) count++;
    if (location) count++;
    return count;
  };

  const renderSkeletons = () => (
    <Grid container spacing={3}>
      {Array.from({ length: 12 }).map((_, index) => (
        <Grid  size={{xs:12, sm:6, md:4, lg:3}}  key={index}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              textAlign: 'center',
            }}
          >
            <Skeleton
              variant="circular"
              width={80}
              height={80}
              sx={{ mx: 'auto', mb: 2 }}
            />
            <Skeleton variant="text" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={36} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          mb: 1,
          color: 'text.primary',
        }}
      >
        {query ? `Search results for "${query}"` : 'People you may know'}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        {loading ? 'Loading...' : `${total} ${query ? 'results' : 'suggestions'} found`}
      </Typography>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showFilters ? 2 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              color="primary"
              size="small"
            >
              <FilterList />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
            {getActiveFiltersCount() > 0 && (
              <Chip
                label={getActiveFiltersCount()}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            )}
          </Box>
          {getActiveFiltersCount() > 0 && (
            <IconButton
              onClick={clearAllFilters}
              size="small"
              color="error"
              title="Clear all filters"
            >
              <Clear />
            </IconButton>
          )}
        </Box>

        {showFilters && (
          <Grid container spacing={2}>
            {/* Work Mode Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Work Mode"
                value={workMode}
                onChange={(e) => {
                  setWorkMode(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="remote">Remote</MenuItem>
                <MenuItem value="onsite">On-site</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </TextField>
            </Grid>

            {/* Notice Type Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Notice Period"
                value={noticeType}
                onChange={(e) => {
                  setNoticeType(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="immediate">Immediate</MenuItem>
                <MenuItem value="15-days">15 Days</MenuItem>
                <MenuItem value="30-days">30 Days</MenuItem>
                <MenuItem value="60-days">60 Days</MenuItem>
                <MenuItem value="90-days">90 Days</MenuItem>
              </TextField>
            </Grid>

            {/* Job Type Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Job Type"
                value={jobType}
                onChange={(e) => {
                  setJobType(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="full-time">Full-time</MenuItem>
                <MenuItem value="part-time">Part-time</MenuItem>
                <MenuItem value="contract">Contract</MenuItem>
                <MenuItem value="freelance">Freelance</MenuItem>
                <MenuItem value="internship">Internship</MenuItem>
              </TextField>
            </Grid>

            {/* Experience Level Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Experience Level"
                value={experienceLevel}
                onChange={(e) => {
                  setExperienceLevel(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="entry">Fresher / Entry Level – 0 to 1 Year</MenuItem>
                <MenuItem value="junior">Junior Level – 1 to 3 Years</MenuItem>
                <MenuItem value="mid">Mid-Level – 3 to 5 Years</MenuItem>
                <MenuItem value="senior">Senior Level – 5 to 8 Years</MenuItem>
                <MenuItem value="manager">Managerial Level – 8 to 12 Years</MenuItem>
                <MenuItem value="head">Leadership / Head Level – 12 to 18 Years</MenuItem>
                <MenuItem value="executive">Director / C-Level – 18+ Years</MenuItem>
              </TextField>
            </Grid>

            {/* Location Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Location"
                placeholder="Enter city or state"
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setPage(1);
                }}
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      {loading ? (
        renderSkeletons()
      ) : users.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {query ? 'No users found' : 'No suggestions available'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {query ? 'Try searching with different keywords' : 'Check back later for new suggestions'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {users.map((user) => (
              <Grid size={{xs:12, sm:6, md:4, lg:4}} key={user._id}>
                <ProfileCard 
                  user={user} 
                  variant={query ? 'search' : 'suggestion'} 
                />
              </Grid>
            ))}
          </Grid>

          {/* {totalPages > 1 && ( */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          {/* )} */}
        </>
      )}
    </Container>
  );
};